/*
 * b2Sei.js — b2 SEI extractor for the recorded WebCodecs player.
 *
 * Parses mpengine's user_data_unregistered SEI NAL out of an MP4 video sample.
 * Ported from the dragon-pilot Studio player (aws/frontend/src/lib/player/b2-sei.ts)
 * to plain JS for dview-lite (CRA / react-scripts, no TypeScript).
 *
 * Producer reference: mpengine/src/b2.c (build_h264_sei_nal, build_hevc_sei_nal,
 * escape_sei_payload). Consumer reference: dview-ffmpeg/csrc/ffmpeg/B2.c.
 *
 * Two-stage byte transform on read:
 *   1. NAL-level: find SEI NAL by nal_type (codec-dispatched). MP4 stores NALs
 *      length-prefixed WITHOUT standard AnnexB RBSP escaping for mpengine output,
 *      so RBSP unescape is OFF by default (it would corrupt valid 00 00 03 in the
 *      payload). Pass { rbspUnescape: true } for a raw AnnexB source.
 *   2. Payload-level: mpengine's escape_sei_payload rewrites 00 00 01 -> AA AA AB
 *      in the b2 body. We reverse this after UUID match, before header parse.
 *
 * Metadata is keyed downstream by composition timestamp (cts), NOT sample index,
 * so B-frame display order resolves to the correct camera name / overlay.
 */

// UUID mpengine writes into every user_data_unregistered SEI (b2.c uuid_iso_iec_11578).
export const B2_UUID = new Uint8Array([
	0xAA, 0xFF, 0xAA, 0xFF, 0xAA, 0xFF, 0xAA, 0xFF,
	0xAA, 0xFF, 0xAA, 0xFF, 0xAA, 0xFF, 0xAA, 0xFF,
]);

// 00 00 01 b2 — the dvs[4] prefix mpengine writes. After payload unescape, the
// b2 header starts with these bytes.
const B2_HEADER_PREFIX = new Uint8Array([0x00, 0x00, 0x01, 0xb2]);

// V2 B2_HEADER_DVS packed struct size (b2.h):
//   dvs[4] + version(1) + rec_flag(1) + ext_flag(1) + vmd(1) + timestamp(4)
//   + name[32] + gmtoffset(1) + padding[3] = 48 bytes
export const B2_HEADER_V2_SIZE = 48;
// V1 header (dvs[4] + version + rec_flag + ext_flag + vmd + timestamp(4) + name[34]).
export const B2_HEADER_V1_SIZE = 46;

// Anti-runaway guard on the extension loop.
const MAX_EXT_LOOP = 32;

// SEI ext_id byte values from b2.h.
export const B2_EXT = {
	NONE: 0x00,
	OVERLAY: 0x01,
	DRIVE: 0x02,
	POS: 0x04,
	VMD: 0x08,
	VMD2: 0x10,
	LPR: 0x20,
	WATERMARK: 0x40,
	OD: 0x80,
};

// Fixed-length extension struct sizes, in bytes. Variable-length (OVERLAY / POS)
// read their dynamic size from line_width * line_count after the header.
const EXT_SIZES = {
	VMD: 30,
	LPR: 95,       // packed; NOT 103 (trailing 8 bytes don't exist)
	OD_V1: 14,
	OD_V2: 50,
	WATERMARK: 10,
	OVERLAY_HDR: 12,
	DRIVE: 44,     // NOT packed: 2 pad bytes after version so x_loc aligns to 4
	POS_HDR: 48,
};

// Decode a fault-tolerant UTF-8 C-string, slicing to the first null byte.
function decodeCString(bytes) {
	const nullIdx = bytes.indexOf(0);
	const end = nullIdx === -1 ? bytes.length : nullIdx;
	return new TextDecoder("utf-8", { fatal: false }).decode(bytes.subarray(0, end));
}

function readU16LE(bytes, o) {
	return (bytes[o] | (bytes[o + 1] << 8)) >>> 0;
}
function readU32LE(bytes, o) {
	return (
		(bytes[o] |
			(bytes[o + 1] << 8) |
			(bytes[o + 2] << 16) |
			(bytes[o + 3] << 24)) >>>
		0
	);
}

// Strip H.264/HEVC RBSP emulation-prevention bytes: every 00 00 03 -> 00 00.
export function unescapeRbsp(bytes) {
	const out = new Uint8Array(bytes.length);
	let o = 0;
	for (let i = 0; i < bytes.length; i++) {
		if (
			i + 2 < bytes.length &&
			bytes[i] === 0x00 &&
			bytes[i + 1] === 0x00 &&
			bytes[i + 2] === 0x03
		) {
			out[o++] = 0x00;
			out[o++] = 0x00;
			i += 2; // skip the 0x03
		} else {
			out[o++] = bytes[i];
		}
	}
	return out.subarray(0, o);
}

// Reverse mpengine's escape_sei_payload: every AA AA AB -> 00 00 01.
export function unescapeB2Payload(bytes) {
	const out = new Uint8Array(bytes.length);
	let o = 0;
	for (let i = 0; i < bytes.length; i++) {
		if (
			i + 2 < bytes.length &&
			bytes[i] === 0xAA &&
			bytes[i + 1] === 0xAA &&
			bytes[i + 2] === 0xAB
		) {
			out[o++] = 0x00;
			out[o++] = 0x00;
			out[o++] = 0x01;
			i += 2;
		} else {
			out[o++] = bytes[i];
		}
	}
	return out.subarray(0, o);
}

// nalBody starts at the NAL header byte. headerLen is 1 (H.264) or 2 (HEVC).
// Returns { codec, payload } (payload AFTER the UUID), or null if no match.
function tryParseSei(nalBody, headerLen, codec, opts) {
	const body = opts.rbspUnescape ? unescapeRbsp(nalBody) : nalBody;
	if (body.length < headerLen + 2) return null;

	let p = headerLen;
	// SEI message: payload_type (ff*N + final), payload_size (ff*N + final), payload.
	let payloadType = 0;
	while (p < body.length && body[p] === 0xFF) {
		payloadType += 0xFF;
		p++;
	}
	if (p >= body.length) return null;
	payloadType += body[p];
	p++;
	if (payloadType !== 5) return null; // user_data_unregistered only

	let payloadSize = 0;
	while (p < body.length && body[p] === 0xFF) {
		payloadSize += 0xFF;
		p++;
	}
	if (p >= body.length) return null;
	payloadSize += body[p];
	p++;

	// Need UUID + payload bytes.
	if (p + 16 > body.length) return null;
	for (let i = 0; i < 16; i++) {
		if (body[p + i] !== B2_UUID[i]) return null;
	}
	p += 16;

	const userDataLen = Math.max(0, payloadSize - 16);
	const end = Math.min(p + userDataLen, body.length);
	return { codec, payload: body.subarray(p, end) };
}

function findSeiLengthPrefixed(bytes, codec, opts) {
	const nalLengthSize = opts.nalLengthSize || 4;
	let p = 0;
	while (p + nalLengthSize < bytes.length) {
		let nalSize = 0;
		for (let i = 0; i < nalLengthSize; i++) nalSize = (nalSize << 8) | bytes[p + i];
		nalSize >>>= 0;
		p += nalLengthSize;
		if (nalSize <= 0 || p + nalSize > bytes.length) return null;
		const nalEnd = p + nalSize;
		const nalBody = bytes.subarray(p, nalEnd);

		if (codec === "h264") {
			const nalType = nalBody[0] & 0x1F;
			if (nalType === 6 /* SEI */) {
				const sei = tryParseSei(nalBody, 1, codec, opts);
				if (sei) return sei;
			} else if (nalType === 1 || nalType === 5 /* VCL slice */) {
				return null; // bail-early
			}
		} else {
			// HEVC: 2-byte NAL header, nal_type = (byte0 >> 1) & 0x3F
			const nalType = (nalBody[0] >> 1) & 0x3F;
			if (nalType === 39 || nalType === 40 /* prefix/suffix SEI */) {
				const sei = tryParseSei(nalBody, 2, codec, opts);
				if (sei) return sei;
			} else if (nalType <= 31 /* VCL slice */) {
				return null;
			}
		}

		p = nalEnd;
	}
	return null;
}

function findSeiAnnexB(bytes, codec, opts) {
	const starts = [];
	let p = 0;
	while (p + 3 <= bytes.length) {
		if (
			bytes[p] === 0x00 &&
			bytes[p + 1] === 0x00 &&
			bytes[p + 2] === 0x00 &&
			bytes[p + 3] === 0x01
		) {
			starts.push({ offset: p + 4, hdrLen: 4 });
			p += 4;
		} else if (
			bytes[p] === 0x00 &&
			bytes[p + 1] === 0x00 &&
			bytes[p + 2] === 0x01
		) {
			starts.push({ offset: p + 3, hdrLen: 3 });
			p += 3;
		} else {
			p++;
		}
	}
	for (let i = 0; i < starts.length; i++) {
		const start = starts[i].offset;
		const end = i + 1 < starts.length ? starts[i + 1].offset - starts[i + 1].hdrLen : bytes.length;
		const nalBody = bytes.subarray(start, end);
		if (codec === "h264") {
			const nalType = nalBody[0] & 0x1F;
			if (nalType === 6) {
				const sei = tryParseSei(nalBody, 1, codec, opts);
				if (sei) return sei;
			} else if (nalType === 1 || nalType === 5) {
				return null;
			}
		} else {
			const nalType = (nalBody[0] >> 1) & 0x3F;
			if (nalType === 39 || nalType === 40) {
				const sei = tryParseSei(nalBody, 2, codec, opts);
				if (sei) return sei;
			} else if (nalType <= 31) {
				return null;
			}
		}
	}
	return null;
}

// Walk an MP4 sample (length-prefixed NALs) or AnnexB byte stream and return the
// first user_data_unregistered SEI matching our UUID. Bails early after first SEI
// hit OR first VCL NAL. codec is "h264" | "hevc".
export function findSeiInSample(bytes, codec, opts = {}) {
	if (bytes.length < 5) return null;
	const isAnnexB =
		(bytes[0] === 0x00 && bytes[1] === 0x00 && bytes[2] === 0x01) ||
		(bytes[0] === 0x00 && bytes[1] === 0x00 && bytes[2] === 0x00 && bytes[3] === 0x01);
	if (isAnnexB) return findSeiAnnexB(bytes, codec, opts);
	return findSeiLengthPrefixed(bytes, codec, opts);
}

// Parse a V2 (or V1 fallback) B2_HEADER_DVS from already-unescaped bytes.
// Returns a header object, or { fallback: true, version } when unsupported.
export function parseB2Header(bytes) {
	if (bytes.length < 5) return { fallback: true, version: -1 };

	if (
		bytes[0] !== B2_HEADER_PREFIX[0] ||
		bytes[1] !== B2_HEADER_PREFIX[1] ||
		bytes[2] !== B2_HEADER_PREFIX[2] ||
		bytes[3] !== B2_HEADER_PREFIX[3]
	) {
		return { fallback: true, version: bytes[4] };
	}

	const version = bytes[4];
	if (version !== 0x02) {
		return { fallback: true, version };
	}
	if (bytes.length < B2_HEADER_V2_SIZE) {
		return { fallback: true, version };
	}

	const rec_flag = bytes[5];
	const ext_flag = bytes[6];
	const vmd = bytes[7];
	const timestamp =
		bytes[8] |
		(bytes[9] << 8) |
		(bytes[10] << 16) |
		((bytes[11] << 24) >>> 0);
	const name = decodeCString(bytes.subarray(12, 12 + 32));
	const gmtoffset = (bytes[44] << 24) >> 24; // signed byte

	return {
		version,
		rec_flag,
		ext_flag,
		vmd,
		timestamp: timestamp >>> 0,
		name,
		gmtoffset,
	};
}

function parseVmd(bytes, p) {
	const version = bytes[p + 1];
	const window_id = bytes[p + 2];
	const sx = readU16LE(bytes, p + 3);
	const sy = readU16LE(bytes, p + 5);
	const ex = readU16LE(bytes, p + 7);
	const ey = readU16LE(bytes, p + 9);
	const sens = [];
	for (let i = 0; i < 5; i++) sens.push(readU16LE(bytes, p + 11 + i * 2));
	const trigger = [];
	for (let i = 0; i < 5; i++) trigger.push(bytes[p + 21 + i]);
	const motion_level = readU16LE(bytes, p + 26);
	const tripwire = bytes[p + 28];
	const directional = bytes[p + 29];
	return { version, window_id, sx, sy, ex, ey, sens, trigger, motion_level, tripwire, directional };
}

function parseLpr(bytes, p) {
	const version = bytes[p + 1];
	const last_hit_lpn = decodeCString(bytes.subarray(p + 2, p + 34));
	const lpn = decodeCString(bytes.subarray(p + 34, p + 66));
	const confidence = readU16LE(bytes, p + 66);
	const state = bytes[p + 68];
	const detection_time = readU16LE(bytes, p + 69);
	const p1_x = readU16LE(bytes, p + 71);
	const p1_y = readU16LE(bytes, p + 73);
	const p2_x = readU16LE(bytes, p + 75);
	const p2_y = readU16LE(bytes, p + 77);
	const p3_x = readU16LE(bytes, p + 79);
	const p3_y = readU16LE(bytes, p + 81);
	const p4_x = readU16LE(bytes, p + 83);
	const p4_y = readU16LE(bytes, p + 85);
	const roi_sx = readU16LE(bytes, p + 87);
	const roi_sy = readU16LE(bytes, p + 89);
	const roi_ex = readU16LE(bytes, p + 91);
	const roi_ey = readU16LE(bytes, p + 93);
	return {
		version, last_hit_lpn, lpn, confidence, state, detection_time,
		p1_x, p1_y, p2_x, p2_y, p3_x, p3_y, p4_x, p4_y,
		roi_sx, roi_sy, roi_ex, roi_ey,
	};
}

function parseOverlay(bytes, p) {
	const pos_id = bytes[p + 1];
	const line_width = bytes[p + 2];
	const line_count = bytes[p + 3];
	const x_loc = readU32LE(bytes, p + 4);
	const y_loc = readU32LE(bytes, p + 8);
	const linesStart = p + EXT_SIZES.OVERLAY_HDR;
	const linesLen = line_width * line_count;
	if (linesStart + linesLen > bytes.length) return null;
	const lines = [];
	for (let i = 0; i < line_count; i++) {
		const lineStart = linesStart + i * line_width;
		lines.push(decodeCString(bytes.subarray(lineStart, lineStart + line_width)));
	}
	return {
		ext: { pos_id, line_width, line_count, x_loc, y_loc, lines },
		size: EXT_SIZES.OVERLAY_HDR + linesLen,
	};
}

function parseDrive(bytes, p) {
	const version = bytes[p + 1];
	const x_loc = readU32LE(bytes, p + 4);
	const y_loc = readU32LE(bytes, p + 8);
	const timestr = decodeCString(bytes.subarray(p + 12, p + 44));
	return { version, x_loc, y_loc, timestr };
}

function parsePos(bytes, p) {
	const version = bytes[p + 1];
	const pos_id = bytes[p + 2];
	const line_width = bytes[p + 3];
	const line_count = bytes[p + 4];
	// res[3] at p+5..p+7
	const x_loc = readU32LE(bytes, p + 8);
	const y_loc = readU32LE(bytes, p + 12);
	const name = decodeCString(bytes.subarray(p + 16, p + 48));
	const linesStart = p + EXT_SIZES.POS_HDR;
	const linesLen = line_width * line_count;
	if (linesStart + linesLen > bytes.length) return null;
	const lines = [];
	for (let i = 0; i < line_count; i++) {
		const lineStart = linesStart + i * line_width;
		lines.push(decodeCString(bytes.subarray(lineStart, lineStart + line_width)));
	}
	return {
		ext: { version, pos_id, line_width, line_count, x_loc, y_loc, name, lines },
		size: EXT_SIZES.POS_HDR + linesLen,
	};
}

function parseWatermark(bytes, p) {
	const version = bytes[p + 1];
	const hash = readU32LE(bytes, p + 2);
	const hash2 = readU32LE(bytes, p + 6);
	return { version, hash, hash2 };
}

function parseOd(bytes, p) {
	const version = bytes[p + 1];
	if (version === 1) {
		const x = readU16LE(bytes, p + 2);
		const y = readU16LE(bytes, p + 4);
		const width = readU16LE(bytes, p + 6);
		const height = readU16LE(bytes, p + 8);
		const cls = (readU16LE(bytes, p + 10) << 16) >> 16; // sign-extend int16
		return { ext: { version: 1, x, y, width, height, cls }, size: EXT_SIZES.OD_V1 };
	}
	if (version === 2) {
		const x = readU16LE(bytes, p + 2);
		const y = readU16LE(bytes, p + 4);
		const width = readU16LE(bytes, p + 6);
		const height = readU16LE(bytes, p + 8);
		const cls_name = decodeCString(bytes.subarray(p + 10, p + 42));
		const cls = (readU16LE(bytes, p + 42) << 16) >> 16;
		const flags = (readU16LE(bytes, p + 44) << 16) >> 16;
		const color = readU32LE(bytes, p + 46);
		return {
			ext: { version: 2, x, y, width, height, cls_name, cls, flags, color },
			size: EXT_SIZES.OD_V2,
		};
	}
	return null;
}

// Walk the extension byte stream after the header. Dispatch on ext_id at
// bytes[cursor], NOT on bit position in extFlag (extFlag is advisory). Each
// extension struct starts with `char ext_id` (b2.h).
export function parseB2Extensions(bytes, extFlag) {
	const out = {};
	let cursor = 0;
	let loops = 0;
	void extFlag; // advisory only; trust the stream byte
	while (cursor < bytes.length && loops++ < MAX_EXT_LOOP) {
		const id = bytes[cursor];
		if (id === 0x00) break;

		if (id === B2_EXT.OVERLAY) {
			const result = parseOverlay(bytes, cursor);
			if (!result) break;
			out.overlay = result.ext;
			cursor += result.size;
		} else if (id === B2_EXT.DRIVE) {
			if (cursor + EXT_SIZES.DRIVE > bytes.length) break;
			out.drive = parseDrive(bytes, cursor);
			cursor += EXT_SIZES.DRIVE;
		} else if (id === B2_EXT.POS) {
			const result = parsePos(bytes, cursor);
			if (!result) break;
			(out.pos || (out.pos = [])).push(result.ext);
			cursor += result.size;
		} else if (id === B2_EXT.VMD) {
			if (cursor + EXT_SIZES.VMD > bytes.length) break;
			(out.vmd || (out.vmd = [])).push(parseVmd(bytes, cursor));
			cursor += EXT_SIZES.VMD;
		} else if (id === B2_EXT.VMD2) {
			if (cursor + EXT_SIZES.VMD > bytes.length) break;
			(out.vmd2 || (out.vmd2 = [])).push(parseVmd(bytes, cursor));
			cursor += EXT_SIZES.VMD;
		} else if (id === B2_EXT.LPR) {
			if (cursor + EXT_SIZES.LPR > bytes.length) break;
			out.lpr = parseLpr(bytes, cursor);
			cursor += EXT_SIZES.LPR;
		} else if (id === B2_EXT.WATERMARK) {
			if (cursor + EXT_SIZES.WATERMARK > bytes.length) break;
			out.watermark = parseWatermark(bytes, cursor);
			cursor += EXT_SIZES.WATERMARK;
		} else if (id === B2_EXT.OD) {
			const od = parseOd(bytes, cursor);
			if (!od) break;
			(out.od || (out.od = [])).push(od.ext);
			cursor += od.size;
		} else {
			// Unknown ext_id — stop, don't try to recover (we'd misalign the stream).
			break;
		}
	}
	return out;
}

let _v1FallbackLogged = false;

// One-call helper: take raw sample bytes, find SEI, unescape, parse header +
// extensions. Returns { header, extensions } or null if no valid b2 SEI present.
export function parseB2FromSample(sampleBytes, codec, opts = {}) {
	const sei = findSeiInSample(sampleBytes, codec, opts);
	if (!sei) return null;
	const unescaped = unescapeB2Payload(sei.payload);
	const headerResult = parseB2Header(unescaped);
	if (headerResult.fallback) {
		if (!_v1FallbackLogged) {
			_v1FallbackLogged = true;
			console.warn(
				`[b2Sei] header version=${headerResult.version} not supported — falling back to event-row HUD`,
			);
		}
		return null;
	}
	const extensions = parseB2Extensions(
		unescaped.subarray(B2_HEADER_V2_SIZE),
		headerResult.ext_flag,
	);
	return { header: headerResult, extensions };
}

// For tests: reset the once-per-process V1 fallback warning state.
export function _resetV1FallbackLog() {
	_v1FallbackLogged = false;
}

// Convert a parsed b2 SEI ({ header, extensions }) into the metadata shape the
// shared overlayRenderer.drawOverlays() consumes, so SEI-sourced OD boxes + LPR
// plates draw identically to the JSON-metadata-track path. b2 coordinates are
// 0..999 (mpengine convention: (v * (width + 0.5)) / 999); overlayRenderer wants
// normalized 0..1, so we divide by 999. Also surfaces camname/time for the HUD.
export function b2ToOverlayMetadata(parsed) {
	if (!parsed) return null;
	const h = parsed.header || {};
	const ext = parsed.extensions || {};
	const md = {
		camname: (h.name || '').trim(),
		time: h.timestamp || 0,
		gmtoffset: h.gmtoffset || 0,
	};

	if (ext.od && ext.od.length) {
		md.objects = ext.od.map((od, i) => {
			const obj = {
				id: `od-${i}`,
				label: od.version === 2 && od.cls_name ? od.cls_name : `cls ${od.cls}`,
				bbox: [od.x / 999, od.y / 999, od.width / 999, od.height / 999],
				class: od.cls,
			};
			if (od.version === 2) {
				obj.flags = od.flags;
				// overlayRenderer reads obj.color as a number, masking to 0xffffff.
				if (od.color) obj.color = od.color & 0xffffff;
			}
			return obj;
		});
	}

	if (ext.lpr) {
		const l = ext.lpr;
		// SEI state enum (0 waiting, 1 detecting, 2+ confirmed) → renderer states.
		const state = l.state >= 2 ? 'HIT' : (l.state === 1 ? 'DETECTING' : 'WAITING');
		const lpr = { state, plate: l.lpn || '' };
		if (l.roi_ex > l.roi_sx && l.roi_ey > l.roi_sy) {
			lpr.roi = [
				l.roi_sx / 999,
				l.roi_sy / 999,
				(l.roi_ex - l.roi_sx) / 999,
				(l.roi_ey - l.roi_sy) / 999,
			];
		}
		lpr.results = [{
			plate: l.lpn || '',
			coordinates: [
				{ x: l.p1_x / 999, y: l.p1_y / 999 },
				{ x: l.p2_x / 999, y: l.p2_y / 999 },
				{ x: l.p3_x / 999, y: l.p3_y / 999 },
				{ x: l.p4_x / 999, y: l.p4_y / 999 },
			],
		}];
		md.lpr = lpr;
	}

	return md;
}

// Format the camera-local wall clock carried in B2 without depending on the
// viewer computer's timezone. B2 stores a UTC epoch plus the NVR GMT offset.
export function formatB2Timestamp(timestamp, gmtoffset = 0) {
	const epochSeconds = Number(timestamp);
	const offsetHours = Number(gmtoffset);
	if (!Number.isFinite(epochSeconds) || epochSeconds <= 0) return '';
	const adjusted = new Date((epochSeconds + (Number.isFinite(offsetHours) ? offsetHours : 0) * 3600) * 1000);
	if (Number.isNaN(adjusted.getTime())) return '';
	const pad2 = (value) => String(value).padStart(2, '0');
	return `${pad2(adjusted.getUTCMonth() + 1)}/${pad2(adjusted.getUTCDate())}/${adjusted.getUTCFullYear()} ` +
		`${pad2(adjusted.getUTCHours())}:${pad2(adjusted.getUTCMinutes())}:${pad2(adjusted.getUTCSeconds())}`;
}
